import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, FileText, Mail, Phone } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LenderMatching = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { lead, matches } = location.state || {};
  
  const [showTermSheetDialog, setShowTermSheetDialog] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [termSheetData, setTermSheetData] = useState({
    loan_amount: '',
    property_address: '',
    ltv: ''
  });

  if (!lead || !matches) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">No matching data available</p>
      </div>
    );
  }

  const handleCreateTermSheet = (match) => {
    setSelectedMatch(match);
    setTermSheetData({
      loan_amount: lead.amount_needed?.replace(/[^0-9]/g, '') || '',
      property_address: lead.property_address || '',
      ltv: '75'
    });
    setShowTermSheetDialog(true);
  };

  const handleSubmitTermSheet = async () => {
    try {
      const response = await axios.post(
        `${API}/term-sheets`,
        {
          lead_id: lead.lead_id,
          lender_id: selectedMatch.lender.lender_id,
          program_id: selectedMatch.program.program_id,
          loan_amount: parseFloat(termSheetData.loan_amount),
          property_address: termSheetData.property_address,
          ltv: parseFloat(termSheetData.ltv)
        },
        { withCredentials: true }
      );

      toast.success('Term sheet created successfully!');
      setShowTermSheetDialog(false);
      
      // Navigate to term sheets view
      navigate('/term-sheets', { state: { leadId: lead.lead_id } });
    } catch (error) {
      toast.error('Failed to create term sheet');
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-primary">
            Lender Matches for {lead.first_name} {lead.last_name}
          </h1>
          <p className="text-muted-foreground mt-2">
            Found {matches.total_matches} matching programs
          </p>
        </div>

        {/* Lead Summary */}
        <Card className="border-primary/30 bg-primary/5">
          <CardHeader>
            <CardTitle>Lead Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-muted-foreground">Credit Score</Label>
                <p className="font-medium">{lead.fico_range || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Loan Amount</Label>
                <p className="font-medium">{lead.amount_needed || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Purpose</Label>
                <p className="font-medium">{lead.reason_for_capital}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Property Type</Label>
                <p className="font-medium">{lead.property_type || 'N/A'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Matches */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {matches.matches.map((match, index) => (
            <Card key={index} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">{match.lender.lender_name}</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      {match.program.program_name}
                    </p>
                  </div>
                  <Badge variant="secondary" className="bg-green-100 text-green-700">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Match
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Program Details */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <Label className="text-muted-foreground">Loan Type</Label>
                    <p className="font-medium">{match.program.loan_type}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Interest Rate</Label>
                    <p className="font-medium text-primary">{match.program.interest_rate}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Term</Label>
                    <p className="font-medium">{match.program.term_months} months</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Max LTV</Label>
                    <p className="font-medium">{match.program.max_ltv}%</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Loan Range</Label>
                    <p className="font-medium text-xs">
                      ${(match.program.min_loan_amount / 1000).toFixed(0)}K - ${(match.program.max_loan_amount / 1000).toFixed(0)}K
                    </p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Min FICO</Label>
                    <p className="font-medium">{match.program.min_fico}</p>
                  </div>
                </div>

                {/* Lender Contact */}
                <div className="pt-3 border-t space-y-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Mail className="h-3 w-3" />
                    {match.lender.email}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Phone className="h-3 w-3" />
                    {match.lender.phone}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Rep: {match.lender.rep_name}
                  </p>
                </div>

                {/* Action Button */}
                <Button 
                  className="w-full gap-2" 
                  onClick={() => handleCreateTermSheet(match)}
                >
                  <FileText className="h-4 w-4" />
                  Create Term Sheet
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {matches.matches.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">
                No matching programs found for this lead's criteria.
                Try adjusting the loan amount or credit requirements.
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create Term Sheet Dialog */}
      <Dialog open={showTermSheetDialog} onOpenChange={setShowTermSheetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Term Sheet</DialogTitle>
            <DialogDescription>
              {selectedMatch && `${selectedMatch.program.program_name} from ${selectedMatch.lender.lender_name}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="loan_amount">Loan Amount *</Label>
              <Input
                id="loan_amount"
                type="number"
                value={termSheetData.loan_amount}
                onChange={(e) => setTermSheetData({ ...termSheetData, loan_amount: e.target.value })}
                placeholder="Enter loan amount"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="property_address">Property Address *</Label>
              <Input
                id="property_address"
                value={termSheetData.property_address}
                onChange={(e) => setTermSheetData({ ...termSheetData, property_address: e.target.value })}
                placeholder="Enter property address"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ltv">Loan-to-Value (LTV) % *</Label>
              <Input
                id="ltv"
                type="number"
                value={termSheetData.ltv}
                onChange={(e) => setTermSheetData({ ...termSheetData, ltv: e.target.value })}
                placeholder="e.g., 75"
                required
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowTermSheetDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSubmitTermSheet}>
                Create Term Sheet
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LenderMatching;
